import {match, Pattern} from 'ts-pattern';

import {always, isNil} from 'ramda';

import type {Nullish} from 'shared/web';

type SliderValueType = [number | null, number | null] | string | null;

type SliderModelObject = {from?: number | null; to?: number | null};
type SliderModel = SliderModelObject | ReadonlyArray<number | null> | string | Nullish;

type SliderModelOutput = {from: number | null; to: number | null} | string | null;

type SliderFilterProps = {
  model: SliderModel;
  onModelChange: (model: SliderModelOutput) => void;
  min: number;
  max: number;
};

const isSliderModelObject = (value: SliderModel): value is SliderModelObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const useSliderFilter = (props: SliderFilterProps) => {
  const parsedModel = isSliderModelObject(props.model) ? props.model : null;
  const model: [number | null, number | null] = [
    parsedModel?.from ?? null,
    parsedModel?.to ?? null,
  ];

  const updateGridFilter = (value: SliderValueType) => {
    const newModel = match(value)
      .with(Pattern.array(), (arrayValue) => {
        const from = arrayValue[0] === 0 ? null : (arrayValue[0] ?? props.min);
        const to = arrayValue[1] === 0 ? null : (arrayValue[1] ?? props.max);

        const isFromNilOrLowestPossible = isNil(from) || from === props.min;
        const isToNilOrLowestPossible = isNil(to) || to === props.max;

        if (isFromNilOrLowestPossible && isToNilOrLowestPossible) {
          return null;
        }

        return {from, to};
      })
      .with(Pattern.string, (stringValue) => stringValue)
      .otherwise(always(null));

    props.onModelChange(newModel);
  };

  return [model, updateGridFilter] as const;
};
